import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import {
  FiInbox,
  FiClock,
  FiBarChart2,
  FiRepeat,
  FiArrowUpCircle,
  FiSettings,
  FiTag,
  FiGrid,
  FiChevronDown,
  FiChevronRight,
  FiChevronLeft,
  FiLogOut,
  FiMenu,
  FiFileText,
  FiUsers,
  FiMessageSquare,
  FiDatabase,
  FiFolder,
  FiSend,
  FiMoon,
  FiSun,
  FiGift,
  FiAward,
  FiTarget,
  FiFlag,
} from 'react-icons/fi'
import { LuBanknote, LuBarcode, LuWallet } from 'react-icons/lu'
import { BsBank2 } from 'react-icons/bs'
import type { IconType } from 'react-icons'
import { EnvSwitchPanel } from './EnvSwitcher'
import { FATURAMENTO_DEVICE_ID } from './ConversasPanel'

// Estrutura de navegação do Módulo Financeiro (PRD §4). `Inadimplentes` fica
// de fora (marcado fora de escopo v1 no PRD).
export type FinView =
  | 'fluxo-caixa' | 'dda' | 'pagar' | 'cobranca' | 'campanhas' | 'boletos' | 'conversas' | 'arquivos' | 'movimentacoes' | 'relatorios' | 'historico'
  | 'conferencia-caixa'
  | 'cad-conta-pj' | 'cad-categorias' | 'cad-centros-custo' | 'cad-favorecidos' | 'dados' | 'incentivos' | 'ativas-semana' | 'vanguard' | 'desafio'

interface NavLeaf { id: FinView; label: string; icon: IconType }
interface NavGroup { id: string; label: string; icon: IconType; children: NavLeaf[] }

// Badges de "novo"/"atualizado" — aparecem sozinhas a partir da data configurada
// e somem POR USUÁRIO quando ele abre o painel (regra "some quando vê", pedido
// do dono 2026-07-10: se o Cleverson abriu, continua aparecendo pro Ricardo até
// ele abrir também). O per-usuário é o localStorage do navegador de cada um —
// sem backend. Pra marcar uma tela como nova ou recém-atualizada: só
// adicionar/trocar a entrada abaixo com a data de hoje + a lista do que mudou
// (`mudancas`).
//
// Dinâmico com o espaço da sidebar: só um badge circular colorido (verde=novo,
// âmbar=atualizado) junto do ícone, antes do título — nada de rótulo de texto
// competindo por espaço com o nome da categoria (o que causava aperto/corte em
// nomes longos). Passar o mouse no badge mostra a lista do que mudou.
const BADGE_CONFIG: Partial<Record<FinView, { tipo: 'novo' | 'atualizado'; desde: string; mudancas: string[] }>> = {
  'desafio': {
    tipo: 'novo', desde: '2026-10-21',
    mudancas: [
      'A planilha do desafio de venda: média das 2 últimas vitrines + incremento, semana a semana, com o realizado do Draco',
      'Regras editáveis: incremento, peso de cada semana, pedido médio, piso e média digitada por grupo',
      'Quem vê o bloco Desafio no Início do app.twms (piloto: RELUZENTE)',
    ],
  },
  'vanguard': {
    tipo: 'novo', desde: '2026-10-06',
    mudancas: [
      'Liga e desliga a aba Vanguard do app: por cargo, distrito, testadores e bloqueados',
      'Escolhe o que a aba mostra, o nome dela e o texto pronto do WhatsApp',
      'Confirma a janela de pagamento de cada vitrine (o Draco só guarda a do último relatório)',
      'Acessos: quem abriu, quando, que grupo olhou e quem chamou no WhatsApp',
    ],
  },
  'incentivos': {
    tipo: 'novo', desde: '2026-09-23',
    mudancas: [
      'As promoções de indicação do Draco, lidas ao vivo: escolha a promoção e veja a regra cadastrada, os brindes de cada faixa e a classificação (o antigo relatório em Excel)',
      'Busca por nome, código ou grupo, filtro por distrito e colunas ordenáveis',
      'Botão Excel baixa a classificação do recorte',
    ],
  },
  'conferencia-caixa': {
    tipo: 'novo', desde: '2026-08-03',
    mudancas: [
      'Conferência de caixa: informe quanto tem no cofre e a diferença contra o sistema aparece na hora',
      'Recontar no mesmo dia corrige a contagem daquele dia, sem empilhar duas verdades',
      'Alerta de saldo negativo: caixa físico não fica no vermelho — quando fica, é entrada que faltou lançar',
      'Histórico de todas as conferências, com o saldo que o sistema mostrava em cada uma',
    ],
  },
  'dados': {
    tipo: 'novo', desde: '2026-07-22',
    mudancas: [
      'Painel Dados (o mesmo do crm.twms): pergunte em português e o assistente monta a consulta no Oracle',
      'A resposta aparece conforme vai sendo gerada, sem esperar o fim',
      'Histórico de consultas, comandos rápidos e voto de útil/inútil pra ensinar o assistente',
    ],
  },
  'arquivos': {
    tipo: 'atualizado', desde: '2026-07-14',
    mudancas: [
      'Busca por nome: digite no campo do topo e Enter — varre a pasta atual e todas as subpastas (acentos e maiúsculas não importam)',
      'Resultado mostra o caminho de cada item; clique numa pasta pra navegar até ela, ou no arquivo pra abrir/baixar direto',
      'Painel Arquivos (o mesmo do crm.twms) — navegue nos compartilhamentos do servidor sem trocar de ambiente',
      'Preview de PDF, vídeo, planilha, Word e imagem; edição via OnlyOffice; upload e versões',
    ],
  },
  'dda': {
    tipo: 'atualizado', desde: '2026-07-20',
    mudancas: [
      'Tela em altura total: barra e cards fixos, tabela com rolagem própria e cabeçalho sempre visível',
      'Navegador de mês padronizado (setas ‹ › + botão Hoje), igual ao Fluxo de caixa e Contas a pagar',
      'Painel DDA: importe o export "DDAObrigacoes" do TotalBank e cada boleto novo vira despesa em aberto no Contas a pagar',
      'Reimportar o mesmo arquivo não duplica (reconhece pela linha digitável)',
      'Categoria sugerida pelas regras da Conciliação; sem regra, entra em "DDA" pra reclassificar depois',
      'Linha digitável copiável direto da tabela, com situação do boleto (em aberto/vencido/pago)',
      'Botão de remover: limpa da lista o boleto que não interessa (apaga a despesa vinculada; reimportar não traz de volta)',
      'Fluxo de curadoria: o import agora só traz os boletos ("Não exportado"); selecione com as caixinhas e use "Exportar p/ Contas a pagar" ou "Excluir" em lote',
      'Coluna "Exportado": check roxo (CP) mostra quem já virou despesa no Contas a pagar',
    ],
  },
  'cad-conta-pj': {
    tipo: 'atualizado', desde: '2026-07-20',
    mudancas: [
      'Tela em altura total e largura cheia: abas fixas, tabela de contas com rolagem própria e cabeçalho sempre visível',
      'Cada conta agora tem campo de Saldo inicial (pode ser negativo) — entra no saldo de abertura do Extrato de movimentações e do Fluxo de caixa',
    ],
  },
  'cad-favorecidos': {
    tipo: 'atualizado', desde: '2026-07-20',
    mudancas: [
      'Tela em altura total e largura cheia: busca, abas e paginação fixas, tabela com rolagem própria e cabeçalho sempre visível',
      'Cadastro de favorecidos com PIX, dados bancários e categoria', 'Importação em massa por planilha (XLS/XLSX/CSV)',
    ],
  },
  'cad-categorias': {
    tipo: 'atualizado', desde: '2026-07-31',
    mudancas: [
      'Novo campo "Grupo DRE" na categoria: define em que linha do DRE comparativo os lançamentos entram (vazio herda do grupo da mãe)',
      'Grupo "Movimentação patrimonial" tira do resultado o que é empréstimo, aplicação, imobilizado e distribuição de lucro',
      'Etiqueta colorida na árvore mostra o grupo DRE de cada grupo (e das exceções que sobrescrevem)',
      'Tela em altura total e largura cheia: as colunas de receita e despesa rolam cada uma por dentro, com o título fixo',
      'Importação do plano de contas por planilha', 'Novo código de redução (Redz.) exibido junto ao código hierárquico',
    ],
  },
  'relatorios': {
    tipo: 'atualizado', desde: '2026-07-31',
    mudancas: [
      'DRE virou comparativo mensal (pedido da diretoria): um mês por coluna, com Total e Média do período',
      'Linhas canônicas com subtotais: Receita Bruta → Deduções → Receita Líquida → Despesas → Resultado Operacional → Resultado Líquido → Margem %',
      'Movimentação patrimonial (empréstimos, aplicações, imobilizado, distribuição de lucro) saiu do resultado e virou bloco informativo à parte',
      'Clique numa linha do DRE para abrir as categorias que a compõem',
      'Exportação em XLSX (matriz + aba Detalhe pra tabela dinâmica) e PDF pronto do servidor (A4 paisagem, estilo diretoria), além do CSV',
    ],
  },
  'pagar': {
    tipo: 'atualizado', desde: '2026-07-22',
    mudancas: [
      'Lista rolável: a página de despesas carrega mais conforme você rola, sem trocar de página (e sem barra de rolagem à vista)',
      'Botão "Duplicados": procura lançamentos repetidos (mesma conta, data, valor e tipo) e deixa você remover — roda sozinho depois de cada importação',
      'Importar não duplica mais: linha já paga que existe no banco é pulada, e a conciliação não cria título em cima de pagamento já lançado',
      'Conciliados: clique no título e o comparativo extrato × lançamento abre logo abaixo, com a diferença de valor destacada',
      'Conciliação com filtro por banco, pra conferir uma conta de cada vez',
      'Tela em altura total: cards, filtros e paginação ficam fixos; só a tabela rola (cabeçalho sempre visível)',
      'Conciliação bancária em MODO LISTA: os cards viraram linhas compactas — logo do banco, data, histórico, valor e as ações (Conciliar/Criar título/Ignorar) na mesma linha; o título casado aparece numa sublinha com o % de confiança',
      'Conciliação bancária e Regras de conciliação no mesmo padrão: barra, chips e avisos fixos; só a lista/tabela rola (cabeçalho das regras sempre visível)',
      'Navegador de mês padronizado: setas ‹ › com o nome do mês, igual ao Fluxo de caixa (sai o campo de mês do navegador)',
      'Selo azul "ASAAS" no lançamento identifica boleto de negociação emitido no Asaas (entra sozinho via sincronização)',
      'Anexos já na CRIAÇÃO do lançamento (foto da fatura, boleto, nota) — tanto na Nova despesa quanto no Criar título da conciliação; sobem junto ao salvar',
      'Campo Fornecedor (nova despesa/criar título) agora autocompleta dos Favorecidos cadastrados — selecionar preenche nome e CPF/CNPJ',
      'Coluna "Banco" na tabela: mostra a conta/banco do pagamento (com o logo)',
      'Ordenação clicável em Vencimento e Situação (clique no cabeçalho; clique de novo inverte)',
      'Conciliação bancária: "Criar título" agora abre o formulário completo de despesa, pré-preenchido com os dados do movimento (a baixa continua vindo da conciliação)',
      'Selo roxo "DDA" no lançamento identifica despesa que veio do painel DDA',
      'Período personalizado: além do mês, dá pra filtrar por intervalo de datas De/Até (vencimento)',
      'Regras de conciliação: botão "todas" no cabeçalho liga/desliga Ativa e Auto-criar em massa',
      'Conciliação bancária (extrato OFX) agora fica aqui, numa aba própria',
      'Sugestão automática de categoria ao criar título a partir do extrato',
      'Importação do histórico de lançamentos do ERP',
    ],
  },
  'cobranca': {
    tipo: 'atualizado', desde: '2026-07-14',
    mudancas: [
      'Painel encaixotado: mesmo layout com respiro lateral e cantos arredondados do Fluxo de caixa, DDA e Contas a pagar',
      'Ícone de dinheiro trocado em todo o sistema (aqui, no crm e no ACE): sai o cifrão de dólar, entra a nota de dinheiro',
      'Visão Análises agora é só métricas: a lista de boletos com reenvio fica oculta enquanto a análise está aberta',
      'Novo quadro "Recuperados após cobrança" no lugar da distribuição do tempo de resposta: cada boleto pago depois de cobrado, marcado Recuperado/Parcial',
      'Medição corrigida: pagamento no mesmo dia e recobranças não viram mais "desperdício" — o número real de recuperação apareceu',
      'Quadro "Intenção de pagamento": quem respondeu após a cobrança (comprovante/disse que pagou) mas ainda sem baixa no DRACO, com link pra conversa — sai da lista quando o pagamento baixa',
    ],
  },
  'conversas': {
    tipo: 'atualizado', desde: '2026-07-20',
    mudancas: [
      'Boleto Asaas na conversa: seção nova no painel do contato (admin) — gera boleto ou parcelamento (carnê) e ARRASTA pro chat pra enviar o PDF; copiar linha digitável e Pix copia-e-cola',
      'Campo Cliente do boleto agora é independente da conversa: digite código, CPF ou NOME de qualquer cliente e o nome + dívida DELE aparecem antes de gerar (caso "líder pede boleto pra consultora")',
      'Consulta Draco: botão na barra do topo — busque qualquer cliente por código, CPF ou nome e veja débitos em aberto E pagamentos recentes (responde "consta pago?" sem trocar de painel)',
      'Painel encaixotado no desktop: mesmo cartão com respiro lateral e cantos arredondados do resto do sistema (no celular segue tela cheia)',
      'Tela em altura total: o header "Conversas WhatsApp + Novo registro" sumiu daqui — todo o espaço vertical é do inbox agora',
      'Notificações (mensagens não entregues / leads aguardando líder) agora são recolhíveis pela filipeta central: viram uma tira compacta com contadores, sem precisar dispensar os avisos',
      'Ações do chat aberto (Arquivar, Selecionar, Atribuir líder) viraram ícones compactos — o nome aparece ao passar o mouse',
      'Toggle de IA por conversa removido do chat — o bot de cobrança é controlado só pelo botão global "Bot cobrança" da barra do topo',
      'Painel focado em cobrança: sumiram o botão "Atribuir líder" e os avisos de leads/líderes do funil do CRM; o alerta de falha de entrega agora mostra só disparos do Faturamento e encaminhamentos',
      'Filtros da lista enxutos: ficaram Empresários, Líderes, Consultoras e Arquivadas — Novos Leads, Aguardando líder e Convertidos (funil do CRM) saíram',
      'Contador vermelho no item "Conversas WhatsApp" da sidebar: quantas conversas aguardam resposta (some quando a tela está aberta; atualiza a cada 30s)',
      'Arquivar conversa resolvida (botão no chat e no hover da lista): sai da lista e VOLTA SOZINHA quando o contato mandar mensagem nova; toggle de arquivadas na barra de filtros',
      'Modo Kanban removido do Faturamento (não era usado)',
      'Lista de conversas e painel do contato agora são retráteis, igual à barra lateral: a lista vira um trilho de avatares clicáveis e o perfil vira o avatar do contato — as setas ficam alinhadas na mesma altura',
      'Painel de conversas WhatsApp do Faturamento (lista, chat, mídia, Kanban), movido do crm.twms.com.br pra cá',
      'O botão "Ver conversa" da Cobrança agora abre a conversa aqui mesmo, sem sair do fin',
      'Envio de arquivo (clip, Ctrl+V de print ou arrastar na conversa) agora aparece na hora na tela — antes a mensagem saía mas só aparecia após F5 se o tempo real estivesse fora',
    ],
  },
  'movimentacoes': {
    tipo: 'atualizado', desde: '2026-07-23',
    mudancas: [
      'Painel de indicadores agora em cascata: Saldo inicial (começo do período) → Receitas realizadas (+) → Despesas realizadas (−) → Saldo final (fim, que vira o começo do período seguinte). Só o realizado move o saldo; "em aberto" (previsto) e o resultado do período ficam numa legenda apagada, sem competir com os saldos',
      'Linhas do extrato mais ricas: logo do banco da conta (transferência mostra origem → destino), selo ASAAS e detalhes — categoria, favorecido, forma de pagamento e vencimento original quando pago fora da data',
      'Tela em altura total e largura cheia: controles e indicadores fixos, tabela com rolagem própria e cabeçalho sempre visível (inclusive na prova real)',
      'Integração Asaas: boletos de negociação viram receitas na conta "Asaas" (recebimento baixa sozinho; taxas viram despesas "Tarifas Asaas") — aparecem aqui, no Fluxo de caixa e nos relatórios',
      'Botão "Extrato Asaas": os SAQUES não viram lançamento sozinhos — só quem conhece a operação sabe se é transferência entre contas ou pagamento a terceiro. O contador no botão mostra quantos estão esperando decisão',
      'Botão "Sincronizar Asaas": busca na hora o que acabou de ser emitido/recebido (o sistema também sincroniza sozinho a cada 15 min)',
      'Filtro por conta: escolha uma ou várias contas pra exibir no extrato (igual ao fluxo de caixa)',
      'Prova real (de↔para): importe o extrato do banco e ele vira uma segunda coluna do próprio extrato, linha a linha — mostrando o que bate, o que só existe de um lado e o fechamento do período nos dois',
      'Período personalizado: além do mês, dá pra filtrar por intervalo de datas De/Até',
      'Lotes rateados detectados sozinhos na prova real: pagamentos individuais (ex.: descontos de líderes) que o banco liquidou agregado viram uma linha de lote expansível — clique pra ver os componentes',
    ],
  },
  'boletos': {
    tipo: 'novo', desde: '2026-07-20',
    mudancas: [
      'Histórico de TODOS os boletos Asaas gerados, com busca por nome/CPF, filtro por período de vencimento e status (pendente, pago, vencido, cancelado)',
      'Agrupado por pessoa: uma linha por cliente com totais (em aberto, pago, vencidos) — clique expande os boletos; a busca já abre expandido',
      'Abrir/baixar o PDF do boleto (e o carnê completo nos parcelamentos) pra reenviar por onde quiser',
      'Cancelar boleto (admin): a cobrança deixa de valer no Asaas e a linha fica vermelha como Cancelado',
      'Excluir (admin): disponível só pra boleto já cancelado — tira a linha do painel de vez',
      'Botão Sincronizar Asaas pra ver na hora o que acabou de ser emitido/pago',
    ],
  },
  'relatorios': {
    tipo: 'atualizado', desde: '2026-07-20',
    mudancas: [
      'Tela em altura total e largura cheia no hub e nos relatórios (DRE e Relação)',
      'DRE em tela larga: Receitas e Despesas lado a lado',
      'Relação a pagar/receber: cabeçalho e linha de Totais sempre visíveis durante a rolagem',
    ],
  },
  'historico': {
    tipo: 'atualizado', desde: '2026-07-20',
    mudancas: [
      'Tela em altura total e largura cheia: filtros e paginação fixos, tabela com rolagem própria e cabeçalho sempre visível',
    ],
  },
  'cad-centros-custo': {
    tipo: 'atualizado', desde: '2026-07-20',
    mudancas: [
      'Tela em altura total e largura cheia: busca, abas e paginação fixas, tabela com rolagem própria e cabeçalho sempre visível',
    ],
  },
  'fluxo-caixa': {
    tipo: 'atualizado', desde: '2026-07-20',
    mudancas: [
      'Tela em altura total: gráfico elástico (cresce com o monitor) e tabela com rolagem própria — cabeçalho e totais sempre visíveis',
      'Análise por categorias repaginada: donut e resumo fixos à esquerda, tabela em altura cheia à direita, ordenação clicável nas colunas, barra de % em cada linha e clique no donut destaca a categoria na tabela',
      'Fundo com pontilhismo sutil em todo o sistema: pontos cinza clarinhos no tema claro e com contraste suave no tema escuro',
      'Telas em altura total: o header com o título e o botão "Novo registro" sumiu de TODOS os painéis — o espaço é do conteúdo agora',
      'Nova despesa/receita continua no Contas a pagar, e Nova transferência no Extrato de movimentações',
      'Filtro por conta agora aceita uma ou várias contas (checkboxes, igual ao Extrato de movimentações) — transferência entre contas selecionadas é neutra no escopo',
      'Período personalizado: além do Diário/Mensal, o botão "Período" filtra por intervalo de datas De/Até (dia a dia, até 366 dias)',
    ],
  },
}

interface BadgeInfo { dotClass: string; tooltip: string }

// "Visto" por usuário via localStorage. A chave inclui o `desde`: uma
// atualização NOVA (data diferente) re-exibe o badge mesmo pra quem já tinha
// visto a anterior. try/catch cobre storage bloqueado (navegação privada etc).
const badgeSeenKey = (view: FinView, desde: string) => `badge_seen:${view}:${desde}`

function badgeVisto(view: FinView): boolean {
  const cfg = BADGE_CONFIG[view]
  if (!cfg) return false
  try { return localStorage.getItem(badgeSeenKey(view, cfg.desde)) !== null } catch { return false }
}

// Grava o "visto" se houver badge pendente pro view. Retorna true se algo
// mudou — sinal pro chamador forçar re-render (localStorage não é reativo).
function marcarBadgeVisto(view: FinView): boolean {
  const cfg = BADGE_CONFIG[view]
  if (!cfg || badgeVisto(view)) return false
  try { localStorage.setItem(badgeSeenKey(view, cfg.desde), new Date().toISOString()) } catch { return false }
  return true
}

function badgeAtivo(view: FinView): BadgeInfo | null {
  const cfg = BADGE_CONFIG[view]
  if (!cfg) return null
  const desdeMs = new Date(`${cfg.desde}T00:00:00`).getTime()
  // Só a partir da data de estreia; daí em diante fica ATÉ este usuário abrir
  // o painel (sem corte de dias — regra "some quando vê").
  if (Date.now() < desdeMs) return null
  if (badgeVisto(view)) return null
  const isNovo = cfg.tipo === 'novo'
  const dataFmt = new Date(desdeMs).toLocaleDateString('pt-BR')
  return {
    dotClass: isNovo ? 'bg-emerald-500' : 'bg-amber-500',
    tooltip: `${isNovo ? 'Novidade' : 'Atualizado'} em ${dataFmt}:\n${cfg.mudancas.map(m => `• ${m}`).join('\n')}`,
  }
}

// Agrega os badges dos filhos no cabeçalho do grupo — senão, com o grupo
// fechado ou a sidebar colapsada, a novidade de um filho fica invisível.
function groupBadgeAtivo(children: NavLeaf[]): BadgeInfo | null {
  const ativos = children.map(c => badgeAtivo(c.id)).filter((b): b is BadgeInfo => !!b)
  if (!ativos.length) return null
  const isNovo = ativos.some(b => b.dotClass === 'bg-emerald-500')
  return {
    dotClass: isNovo ? 'bg-emerald-500' : 'bg-amber-500',
    tooltip: ativos.map(b => b.tooltip).join('\n\n'),
  }
}

// Itens de topo (nível 1) + grupo "Cadastros" (nível 2), na ordem do PRD.
const NAV_ITEMS: NavLeaf[] = [
  { id: 'fluxo-caixa',   label: 'Fluxo de caixa',           icon: FiBarChart2 },
  // 2º da lista a pedido do diretor (2026-07-22): é a tela que ele mais usa.
  { id: 'dados',         label: 'Dados',                    icon: FiDatabase },
  { id: 'dda',           label: 'DDA',                      icon: FiInbox },
  { id: 'pagar',         label: 'Contas a pagar',           icon: FiArrowUpCircle },
  { id: 'cobranca',      label: 'Cobrança',                 icon: LuBanknote },
  { id: 'campanhas',     label: 'Campanhas',                icon: FiSend },
  // Promoções de indicação do Draco (23/09/2026) — só admin, ver filtro abaixo.
  { id: 'incentivos',    label: 'Incentivos',               icon: FiGift },
  // Relatório semanal do diretor (03/10/2026): limite × pedido × retail do Draco. Só admin.
  // Renomeado de "Ativas da semana" em 06/10/2026; o id segue o mesmo (links e permissão).
  { id: 'ativas-semana', label: 'Limite de crédito',        icon: FiAward },
  // A aba Vanguard do app.twms: quem vê, o que mostra, janelas, acessos (06/10/2026). Só admin.
  { id: 'vanguard',      label: 'Vanguard',                 icon: FiTarget },
  // O desafio de pedidos por grupo (a planilha do diretor) + o bloco no app.twms (21/10/2026). Só admin.
  { id: 'desafio',       label: 'Desafio',                  icon: FiFlag },
  { id: 'boletos',       label: 'Boletos gerados',          icon: LuBarcode },
  { id: 'conversas',     label: 'Conversas WhatsApp',       icon: FiMessageSquare },
  { id: 'arquivos',      label: 'Arquivos',                 icon: FiFolder },
  // 'movimentacoes' voltou à nav em 2026-07-07 (ficou oculto de 06 a 07/07
  // enquanto a diretoria decidia se a tela ficava).
  { id: 'movimentacoes', label: 'Extrato de movimentações', icon: FiRepeat },
  // Conta de dinheiro não tem OFX pra conferir contra: quem confere é a contagem
  // física, e é esta tela que a recebe (auditoria da Caixinha, 2026-08-03).
  { id: 'conferencia-caixa', label: 'Conferência de caixa', icon: LuWallet },
  { id: 'relatorios',    label: 'Relatórios',               icon: FiFileText },
  { id: 'historico',     label: 'Histórico',                icon: FiClock },
]

const CADASTROS: NavGroup = {
  id: 'cadastros', label: 'Cadastros', icon: FiSettings,
  children: [
    { id: 'cad-conta-pj',      label: 'Contas e Cobrança',      icon: BsBank2 },
    { id: 'cad-categorias',    label: 'Categorias financeiras', icon: FiTag },
    { id: 'cad-centros-custo', label: 'Centros de custo',       icon: FiGrid },
    { id: 'cad-favorecidos',   label: 'Favorecidos',            icon: FiUsers },
  ],
}

interface FinSidebarProps {
  active: FinView
  onNavigate: (v: FinView) => void
  collapsed: boolean
  onToggleCollapse: () => void
  mobileOpen: boolean
  onCloseMobile: () => void
  // nº de conversas do Faturamento aguardando resposta — badge vermelho no
  // item Conversas (oculto enquanto a própria tela de Conversas está aberta).
  waUnread?: number
}

export function FinSidebar({ active, onNavigate, collapsed, onToggleCollapse, mobileOpen, onCloseMobile, waUnread = 0 }: FinSidebarProps) {
  // A faixa "SCF | SISTEMA DE CONTROLE FINANCEIRO" saiu em 07/08/2026: com a
  // sidebar descolada ela só poluía a placa. Junto foram o filete que a separava
  // do rodapé e todo o maquinário de font-size dinâmico (ref + useLayoutEffect +
  // ResizeObserver) que existia só pra encaixar aquele texto na largura.
  const { user, logout, hasDeviceAccess, hasFolderAccess } = useAuth()
  const { theme, setTheme } = useTheme()
  // Nasce RETRAÍDO (07/08/2026): abrir o grupo por padrão só enchia a barra.
  // A exceção é entrar já dentro de um filho de Cadastros (deep link / última
  // tela): com o grupo fechado os filhos nem são renderizados, então NADA na
  // navegação apareceria como ativo e a barra mentiria sobre onde você está.
  const [cadastrosOpen, setCadastrosOpen] = useState(
    () => CADASTROS.children.some(c => c.id === active))
  const [envOpen, setEnvOpen] = useState(false)

  // Marca o painel ativo como visto — cobre o clique de navegação E o view
  // default de abertura do app num lugar só (o effect roda sempre que `active`
  // muda, inclusive no mount). O tick força re-render pro badge sumir na hora,
  // já que localStorage não é reativo. Abrir um filho de Cadastros marca só
  // aquele filho; o badge agregado do grupo (groupBadgeAtivo) passa a refletir
  // apenas os filhos ainda não vistos.
  const [, setSeenTick] = useState(0)
  useEffect(() => {
    if (marcarBadgeVisto(active)) setSeenTick(t => t + 1)
  }, [active])

  // Conversas WhatsApp exige permissão do device Faturamento no crm (admin tem
  // bypass) — mesma regra de visibilidade que o inbox tinha no crm.twms.
  const navItems = NAV_ITEMS.filter(i =>
    (i.id !== 'conversas' || hasDeviceAccess(FATURAMENTO_DEVICE_ID)) &&
    // Arquivos segue a permissão de pastas do crm (admin tem bypass)
    (i.id !== 'arquivos' || !!user?.isAdmin || hasFolderAccess) &&
    // Dados (Oracle AI) é a mesma tela do crm; lá o acesso é de infraestrutura.
    // Aqui mostramos só pra admin — o backend do crm revalida de qualquer forma.
    (i.id !== 'dados' || !!user?.isAdmin) &&
    // Incentivos: lista de brindes da diretoria. O backend (fin e crm) também barra.
    (i.id !== 'incentivos' || !!user?.isAdmin) &&
    (i.id !== 'ativas-semana' || !!user?.isAdmin) &&
    (i.id !== 'vanguard' || !!user?.isAdmin) &&
    (i.id !== 'desafio' || !!user?.isAdmin))
  const cadastrosChildren = CADASTROS.children

  const userLabel = user?.fullName || user?.username || ''
  const avatarLetter = (user?.fullName || user?.username || '?').charAt(0).toUpperCase()
  const cadastrosActive = CADASTROS.children.some(c => c.id === active)

  const go = (v: FinView) => { onNavigate(v); onCloseMobile() }

  // Item de navegação é <a href> de verdade, não <button>: o App reflete o
  // painel em ?panel=, então cada item TEM endereço — e com o link o navegador
  // passa a oferecer "abrir em nova aba/janela" no botão direito, além de
  // Ctrl+clique e clique do meio (pedido do diretor, 2026-07-21). Clique normal
  // continua SPA. Mesmo padrão do crm.
  const hrefFor = (v: FinView) => `${window.location.pathname}?panel=${v}`
  const navClick = (v: FinView) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Modificador ou botão não-primário: sai do caminho e deixa o navegador
    // abrir a aba — o preventDefault mataria justamente esse comportamento.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()
    go(v)
  }

  const itemClass = (isActive: boolean) => [
    'group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    collapsed ? 'justify-center' : '',
    isActive
      ? 'bg-primary/10 text-primary'
      : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
    isActive ? 'neu-side-item neu-side-active' : 'neu-side-item',
  ].join(' ')

  const content = (
    <aside
      className={[
        // `dark` força o subtree em dark-mode: a sidebar é SEMPRE escura,
        // independente do tema do app (mesmo padrão do header do CRM); o conteúdo
        // é sempre light (app light-only).
        // z-40 > z-30 das colunas do painel de Conversas — a filipeta da
        // sidebar se projeta SOBRE a lista de conversas, então precisa vencer
        // o empate de stacking (empate em z = ordem do DOM, e a lista vem depois).
        // `neu-side-float` descola a placa a partir de `lg`, DESDE QUE a raiz
        // tenha `.neu-stage` (App.tsx). Toda a geometria mora no CSS: aqui é só
        // o token. Ver o bloco SIDEBAR DESCOLADA no index.css.
        'dark relative z-[70] flex h-full flex-col bg-card border-r border-border shrink-0 transition-[width,box-shadow] duration-200 neu-side neu-side-float',
        // Sombra sutil projetando pra direita ("relevo" da sidebar escura sobre o
        // conteúdo claro). Continua aqui porque é o que vale nos dois casos em que
        // a barra é RENTE: a gaveta mobile e o NEU desligado. Descolada, o
        // .neu-side-float a substitui por uma sombra ambiente.
        'shadow-[1px_0_2px_0_rgba(2,6,23,0.38),4px_0_10px_-2px_rgba(2,6,23,0.24),10px_0_22px_-4px_rgba(2,6,23,0.16),18px_0_36px_-8px_rgba(2,6,23,0.10)]',
        collapsed ? 'w-[68px]' : 'w-64',
      ].join(' ')}
    >
      {/* Recolher/expandir — "filipeta": círculo centrado NA linha da borda
          direita (translate-x-1/2), metade dentro da sidebar, metade sobre o
          conteúdo. SEM borda de propósito: o botão é `bg-card` dentro do mesmo
          subtree `dark` da sidebar, então sem contorno a metade de dentro se
          funde ao painel e a de fora lê como saliência dele — com borda, lia
          como botão solto pousado em cima. Herda o dark da sidebar (fica escura
          sobre o conteúdo claro). O z-[70] no <aside> garante que ela pinta
          ACIMA do conteúdo em que se projeta. */}
      <button
        onClick={onToggleCollapse}
        title={collapsed ? 'Expandir' : 'Recolher'}
        className="hidden lg:flex absolute right-0 translate-x-1/2 top-1/2 -translate-y-1/2 z-50 items-center justify-center w-8 h-8 rounded-full bg-card text-muted-foreground hover:text-foreground transition-colors neu-side-flip"
      >
        {collapsed ? <FiChevronRight className="w-4 h-4" /> : <FiChevronLeft className="w-4 h-4" />}
      </button>
      {/* ── Marca (só a logo — o nome do sistema tem linha própria acima do
          rodapé; controles ficam no rodapé) ── */}
      {/* Sem filete divisório (07/08/2026): na placa descolada os três traços
          horizontais eram o que mais poluía. O respiro já separa marca, nav e
          rodapé — a linha só repetia a informação. */}
      <div className="flex items-center justify-center min-h-[88px] px-3 py-3">
        {collapsed ? (
          <img src="/favicon.svg" alt="SCF — Sistema de Controle Financeiro" title="SCF — Sistema de Controle Financeiro" className="h-8 w-8 select-none" draggable={false} />
        ) : (
          <>
            <img
              src="/logo_twms_nova_4-clara.gif"
              alt="TWMS · Campo Grande · Distribuidora autorizada Tupperware"
              className="max-h-16 w-auto max-w-[90%] object-contain select-none dark:hidden"
              draggable={false}
            />
            <img
              src="/logo_twms_nova_4.gif"
              alt="TWMS · Campo Grande · Distribuidora autorizada Tupperware"
              className="max-h-16 w-auto max-w-[90%] object-contain select-none hidden dark:block"
              draggable={false}
            />
          </>
        )}
      </div>

      {/* ── Navegação + painel de troca (expande só no espaço livre abaixo) ── */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <nav className="flex-initial min-h-0 overflow-y-auto px-2 py-3 space-y-1">
        {navItems.map(({ id, label, icon: Icon }) => {
          const isActive = active === id
          const dyn = badgeAtivo(id)
          // Badge "precisa responder": conta as conversas com não lida. Some com
          // a tela de Conversas aberta (lá a própria lista já mostra). Colapsada,
          // o contador toma o lugar do pontinho de changelog (prioridade).
          const unread = id === 'conversas' && !isActive && waUnread > 0 ? waUnread : 0
          const unreadTitle = unread ? `${unread} conversa${unread === 1 ? '' : 's'} aguardando resposta` : ''
          const title = collapsed
            ? [label, unreadTitle, dyn?.tooltip].filter(Boolean).join('\n\n')
            : (unread ? unreadTitle : undefined)
          return (
            <a key={id} href={hrefFor(id)} onClick={navClick(id)} title={title} className={itemClass(isActive)}>
              {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />}
              <span className="relative shrink-0">
                <Icon className="w-[18px] h-[18px]" />
                {unread > 0 && collapsed ? (
                  <span className="neu-tag absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none tabular-nums flex items-center justify-center ring-2 ring-card pointer-events-none">
                    {unread > 99 ? '99+' : unread}
                  </span>
                ) : dyn && (
                  <span title={dyn.tooltip} className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ring-2 ring-card cursor-help ${dyn.dotClass}`} />
                )}
              </span>
              {!collapsed && <span className="truncate flex-1 min-w-0 text-left">{label}</span>}
              {!collapsed && unread > 0 && (
                <span className="neu-tag ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none tabular-nums flex items-center justify-center shrink-0">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </a>
          )
        })}

        {/* ── Grupo Cadastros ── */}
        <div className="pt-1">
          {(() => {
            const groupDyn = groupBadgeAtivo(cadastrosChildren)
            const title = collapsed ? (groupDyn ? `${CADASTROS.label}\n\n${groupDyn.tooltip}` : CADASTROS.label) : undefined
            // Recolhida, a linha do grupo NAVEGA (vai no 1º filho) — então é
            // link, igual aos outros itens. Expandida ela só abre/fecha a lista:
            // continua <button>, porque não tem endereço a oferecer.
            const groupInner = (
              <>
                <span className="relative shrink-0">
                  <CADASTROS.icon className="w-[18px] h-[18px]" />
                  {groupDyn && <span title={groupDyn.tooltip} className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ring-2 ring-card cursor-help ${groupDyn.dotClass}`} />}
                </span>
                {!collapsed && <span className="truncate flex-1 min-w-0 text-left">{CADASTROS.label}</span>}
                {!collapsed && (
                  <span className="ml-auto">
                    {cadastrosOpen ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                  </span>
                )}
              </>
            )
            return collapsed ? (
              <a
                href={hrefFor(cadastrosChildren[0].id)}
                onClick={navClick(cadastrosChildren[0].id)}
                title={title}
                className={itemClass(cadastrosActive)}
              >
                {groupInner}
              </a>
            ) : (
              <button
                onClick={() => setCadastrosOpen(o => !o)}
                title={title}
                className={itemClass(false)}
              >
                {groupInner}
              </button>
            )
          })()}
          {!collapsed && cadastrosOpen && (
            <div className="mt-1 ml-4 pl-3 border-l border-border space-y-1">
              {cadastrosChildren.map(({ id, label, icon: Icon }) => {
                const isActive = active === id
                const dyn = badgeAtivo(id)
                return (
                  <a key={id} href={hrefFor(id)} onClick={navClick(id)} className={itemClass(isActive)}>
                    {isActive && <span className="absolute left-[-13px] top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />}
                    <span className="relative shrink-0">
                      <Icon className="w-4 h-4" />
                      {dyn && <span title={dyn.tooltip} className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ring-2 ring-card cursor-help ${dyn.dotClass}`} />}
                    </span>
                    <span className="truncate flex-1 min-w-0 text-left">{label}</span>
                  </a>
                )
              })}
            </div>
          )}
        </div>
      </nav>
        {/* Sobra da sidebar: o painel ocupa só esse espaço livre, ancorado embaixo
            (cresce do rodapé pra cima). Com o painel aberto, reserva altura mínima
            pra ele inteiro — senão, em tela baixa, o último ambiente fica cortado
            atrás do rodapé (a navegação acima encolhe e rola). */}
        <div className={[
          'flex-1 min-h-0 flex flex-col justify-end overflow-hidden transition-[min-height] duration-[550ms]',
          envOpen ? (collapsed ? 'min-h-[136px]' : 'min-h-[156px]') : '',
        ].join(' ')}>
          {user?.isAdmin && <EnvSwitchPanel open={envOpen} onClose={() => setEnvOpen(false)} collapsed={collapsed} />}
        </div>
      </div>

      {/* ── Rodapé: usuário (avatar + nome) à esquerda · controles à direita ── */}
      {/* Sem filete divisório — ver o comentário na faixa da marca. A vertical
          dos filhos de Cadastros (`border-l`, na lista abaixo do grupo) FICA:
          ali a linha não divide seções, ela mostra a hierarquia. */}
      <div className="relative p-2 neu-side-foot">
        <div className={`flex ${collapsed ? 'flex-col items-center gap-1' : 'items-center gap-2 px-1'}`}>
          {/* Usuário: só o avatar (nome vai no tooltip) */}
          <div className="flex items-center gap-2 min-w-0">
            <div
              title={userLabel}
              className="w-9 h-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0"
            >
              <span className="text-sm font-bold text-primary leading-none">{avatarLetter}</span>
            </div>
          </div>

          {/* Controles: tema · trocar ambiente (admin) · sair (alinhados à direita) */}
          <div className={`flex items-center gap-1 ${collapsed ? 'flex-col' : 'ml-auto'}`}>
            {/* Lua: volta ao rodapé (2026-08-04) junto com o tema escuro em NEU.
                Herda a casca neu dos vizinhos pelo `.neu-side-foot button.rounded-lg`,
                então não precisa de CSS próprio. */}
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? 'Voltar ao tema claro' : 'Usar o tema escuro'}
              aria-label="Alternar tema"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              {theme === 'dark'
                ? <FiSun className="w-[18px] h-[18px]" />
                : <FiMoon className="w-[18px] h-[18px]" />}
            </button>
            {user?.isAdmin && (
              <button
                onClick={() => setEnvOpen(o => !o)}
                title="Trocar de ambiente"
                aria-label="Trocar de ambiente"
                className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${
                  envOpen ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                <FiRepeat className="w-[18px] h-[18px]" />
              </button>
            )}
            <button
              onClick={logout}
              title="Sair"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
            >
              <FiLogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      </div>
    </aside>
  )

  return (
    <>
      {/* Desktop: fixa no fluxo */}
      <div className="hidden lg:flex">{content}</div>

      {/* Mobile: drawer sobre overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={onCloseMobile} />
          <div className="relative">{content}</div>
        </div>
      )}
    </>
  )
}

// Rótulos legíveis por id — usados pela topbar/placeholder de conteúdo.
export const FIN_LABELS: Record<FinView, string> = {
  'dda': 'DDA',
  'pagar': 'Contas a pagar',
  'dados': 'Dados',
  'cobranca': 'Cobrança',
  'campanhas': 'Campanhas',
  'incentivos': 'Incentivos',
  'ativas-semana': 'Limite de crédito',
  'vanguard': 'Vanguard',
  'desafio': 'Desafio',
  'boletos': 'Boletos gerados',
  'conversas': 'Conversas WhatsApp',
  'arquivos': 'Arquivos',
  'movimentacoes': 'Extrato de movimentações',
  'conferencia-caixa': 'Conferência de caixa',
  'fluxo-caixa': 'Fluxo de caixa',
  'relatorios': 'Relatórios',
  'historico': 'Histórico',
  'cad-conta-pj': 'Contas e Cobrança',
  'cad-categorias': 'Categorias financeiras',
  'cad-centros-custo': 'Centros de custo',
  'cad-favorecidos': 'Favorecidos',
}

export { FiMenu }
